const escapeHtml = (value: string) => value
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

export const getPreviewHtml = (body: string) => {
  if (/<(?:!doctype|html|body|main)\b/i.test(body)) return body;

  const headings = Array.from(body.matchAll(/\*\*([\s\S]*?)\*\*/g));
  if (headings.length < 2) {
    return `<!doctype html><html><head><meta charset="utf-8"></head>
      <body style="margin:0;padding:24px 12px;background:#f3f6fa;color:#344256;font:14px/1.55 Arial,Helvetica,sans-serif">
        <main style="box-sizing:border-box;max-width:680px;margin:0 auto;padding:24px 28px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;white-space:pre-wrap">${escapeHtml(body)}</main>
      </body></html>`;
  }

  const [dateHeading, staffHeading, ...sectionHeadings] = headings;
  const date = dateHeading[1];
  const staff = staffHeading[1];
  const contentStart = staffHeading.index! + staffHeading[0].length;
  const legacySections = sectionHeadings.map((heading, index) => {
    const textStart = heading.index! + heading[0].length;
    const nextHeading = sectionHeadings[index + 1];
    const textEnd = nextHeading?.index ?? body.length;
    const detail = body.slice(textStart, textEnd).replace(/^\s*-\s*/, "").trim();
    return { detail };
  });
  const contentBeforeSections = body
    .slice(contentStart, sectionHeadings[0]?.index ?? body.length)
    .replace(/^\s*-\s*/, "")
    .trim();
  const facilityHeading = sectionHeadings[0]?.[1].trim();
  const activityDetails = [
    contentBeforeSections,
    ...legacySections.map(({ detail }) => detail),
  ].filter(Boolean);
  const monthYearPattern = /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{4}\b/i;
  const activityHtml = activityDetails.map((detail) => {
    const normalizedDetail = detail.replace(/\s+/g, " ").trim();
    const monthStart = normalizedDetail.search(monthYearPattern);
    const residentName = monthStart > 0 ? normalizedDetail.slice(0, monthStart).trim() : "";
    const note = residentName ? normalizedDetail.slice(monthStart) : normalizedDetail;
    const noteParts = note.split(/\s·\s/).map((part) => part.trim()).filter(Boolean);
    const messageMonthPattern = new RegExp(`${monthYearPattern.source}:`, "i");
    const paymentStatusPattern = /\b(?:marked as paid|paid status removed)\b/i;
    let paymentActivitySeen = false;
    const formattedNoteParts = noteParts.map((part) => {
      const messageStart = part.search(messageMonthPattern);
      const paymentStatusStart = part.search(paymentStatusPattern);
      const isPaymentActivity = paymentStatusStart >= 0;
      const followsPaymentActivity = paymentActivitySeen ||
        (isPaymentActivity && (messageStart < 0 || paymentStatusStart < messageStart));
      paymentActivitySeen ||= isPaymentActivity;
      if (messageStart < 0 || !followsPaymentActivity) return escapeHtml(part);

      return `${escapeHtml(part.slice(0, messageStart).trim())}<br>${escapeHtml(part.slice(messageStart).trim())}`;
    });

    return `<div style="padding:7px 0;border-bottom:1px solid #edf1f5">
      ${residentName ? `<p style="margin:0 0 2px;color:#1e293b;font-size:13px;line-height:1.35;font-weight:700">${escapeHtml(residentName)}</p>` : ""}
      ${formattedNoteParts.length ? `<p style="margin:0;color:#475569;font-size:12px;line-height:1.45">${formattedNoteParts.join(' <span style="color:#94a3b8">·</span> ')}</p>` : ""}
    </div>`;
  }).join("");
  const activitySection = `<div style="padding:0 12px;border-left:2px solid #dbe5f0">
    ${facilityHeading ? `<h3 style="margin:0 0 12px;padding:10px 12px;border-left:5px solid #2563eb;border-radius:4px;background:#eaf2ff;color:#163b66;font-size:18px;line-height:1.35;font-weight:700">${escapeHtml(facilityHeading)}</h3>` : ""}
    ${activityHtml || `<p style="margin:0;color:#64748b;font-size:12px">No activity details.</p>`}
  </div>`;

  return `<!doctype html><html><head><meta charset="utf-8"></head>
    <body style="margin:0;padding:14px 8px;background:#f3f6fa;color:#344256;font-family:Arial,Helvetica,sans-serif">
      <main style="box-sizing:border-box;max-width:760px;margin:0 auto;padding:22px 28px;background:#fff;border:1px solid #dce4ee;border-radius:8px">
        <header style="margin:0 0 18px;padding:0 0 14px;border-bottom:2px solid #24466d">
          <p style="margin:0 0 5px;color:#64748b;font-size:10px;line-height:1.4;font-weight:700;letter-spacing:1px;text-transform:uppercase">Daily claims report</p>
          <h1 style="margin:0;color:#1e293b;font-size:24px;line-height:1.3;font-weight:700">${escapeHtml(date)}</h1>
        </header>
        <section style="margin:0 0 14px;padding:0 0 12px;border-bottom:1px solid #e2e8f0">
          <h2 style="margin:0 0 12px;padding:10px 14px;border-left:4px solid #3b82f6;background:#f3f7fc;color:#1e293b;font-size:15px;line-height:1.4;font-weight:700">${escapeHtml(staff.replace(/^\s*-\s*/, ""))}</h2>
          <div style="margin:0;padding:14px 16px;border:1px solid #dce4ee;border-radius:6px;background:#fff">
            ${activitySection}
          </div>
        </section>
      </main>
    </body>
  </html>`;
};
